import type { FC, PropsWithChildren, ReactNode } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "@tanstack/react-router";
import { SidebarTrigger } from "./ui/sidebar";
import GlobalSearch from "@/components/globalSearch";
import NotificationBell from "@/components/notifications/NotificationBell";
import TasksBell from "@/components/tasks/TasksBell";

type NavbarContextValue = {
  content: ReactNode;
  setContent: (content: ReactNode) => void;
  hidden: boolean;
  setHidden: (hidden: boolean) => void;
};

const NavbarContext = createContext<NavbarContextValue | null>(null);

export const NavbarProvider: FC<PropsWithChildren> = function ({ children }) {
  const { pathname } = useLocation();
  const [content, setContent] = useState<ReactNode>(null);
  const [hidden, setHidden] = useState(false);
  const previousClientId = useRef<string | null>(null);

  useEffect(() => {
    const match = pathname.match(/^\/clients\/([^/]+)(?:\/|$)/);
    const clientKey = match?.[1] ?? null;
    const isSameClient = clientKey && previousClientId.current === clientKey;

    if (!isSameClient) {
      setContent(null);
    }

    setHidden(false);
    previousClientId.current = clientKey;
  }, [pathname]);

  const value = useMemo(
    () => ({ content, setContent, hidden, setHidden }),
    [content, hidden],
  );

  return (
    <NavbarContext.Provider value={value}>{children}</NavbarContext.Provider>
  );
};

export const useNavbar = () => {
  const context = useContext(NavbarContext);
  if (!context) {
    if (typeof window !== "undefined") {
      console.error("useNavbar must be used within NavbarProvider.");
    }
    return {
      content: null,
      setContent: () => {},
      hidden: false,
      setHidden: () => {},
    };
  }
  return context;
};

const Navbar: FC<PropsWithChildren> = function ({ children }) {
  const { content, hidden } = useNavbar();

  if (hidden) {
    return null;
  }

  return (
    <div className="sticky top-0 z-30 shrink-0 border-b bg-background">
      <div className="w-full h-full px-4 py-1 lg:px-5 lg:pl-3">
        <div className="h-full flex items-center justify-between gap-3">
          <div className="h-full flex items-center gap-3">
            <SidebarTrigger />
            {children ?? content}
          </div>
          <div className="flex items-center gap-2">
            <GlobalSearch iconOnly />
            <TasksBell />
            <NotificationBell />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
